import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import ToastMsg from "../../components/common/ToastMsg";
import { ImagePaths } from "../../utils/ImagePaths";
import CustomOtpInput from "../../components/ui/CustomeOTPInput";
import useApiMethods from "../../lib/useApiMethods";
import { FORGOT_PASSWORD, VERIFY_OTP } from "../../constants/api";

const OtpVerification = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { create } = useApiMethods(); // centralized API method

  const [otp, setOtp] = useState("");
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(60);
  const [timerRunning, setTimerRunning] = useState(false);

  const id = state?.id || 0;
  const email = state?.email || "";

  // Start countdown timer
  useEffect(() => {
    let timer;
    const startTimer = () => {
      setSeconds(60);
      setTimerRunning(true);
      timer = setInterval(() => {
        setSeconds((prev) => {
          if (prev > 0) return prev - 1;
          clearInterval(timer);
          setTimerRunning(false);
          return 0;
        });
      }, 1000);
    };

    startTimer();
    return () => clearInterval(timer);
  }, []);

  // OTP input change
  const handleOtpChange = (value) => setOtp(value);

  // Verify OTP
  const onFinish = async () => {
    if (otp.length !== 4) {
      ToastMsg("error", "Please enter the 4-digit OTP");
      return;
    }

    setLoading(true);
    try {
      const payload = { otp, user_id: id, email };
      const response = await create(VERIFY_OTP, payload, false, setLoading);

      if (response?.status === "success") {
        ToastMsg("success", response.message || "OTP verified successfully");
        navigate("/resetpassword", {
          state: { id, user_name: response.user_name, email },
        });
      } else {
        ToastMsg("error", response.message || "OTP verification failed");
      }
    } catch (error) {
      ToastMsg("error", error.message || "Failed to verify OTP");
    } finally {
      setLoading(false);
    }
  };

  // Resend OTP
  const handleResend = async () => {
    if (timerRunning) return;

    setLoading(true);
    try {
      const payload = { email };
      const response = await create(FORGOT_PASSWORD, payload, false, setLoading);

      if (response?.status === "success") {
        ToastMsg("success", response.message || "OTP sent successfully");
        setSeconds(60);
        setTimerRunning(true);
      } else {
        ToastMsg("error", response.message || "Failed to send OTP");
      }
    } catch (error) {
      ToastMsg("error", error.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex items-center justify-center relative bg-gray-50 dark:bg-gray-900 font-sans">
      {/* Background Image */}
      <div
        className="absolute inset-0 bg-[url('https://media.istockphoto.com/id/1407540811/photo/one-time-password.jpg?s=1024x1024&w=is&k=20&c=Mb8htfVz3o_cx6T13QXl3VRYDYdF8h8yVtllaopY0Ak=')] 
    bg-cover bg-center opacity-10"
      />
      <div className="absolute inset-0 bg-white/40 dark:bg-black/40 backdrop-blur-sm" />

      {/* Card */}
      <div className="w-full max-w-sm relative z-10">
        {/* Logo */}
        <div className="flex justify-center mb-6">
          <img
            src={ImagePaths.loginLogo.default}
            alt="App Logo"
            className="h-10 w-auto animate-fadeIn"
          />
        </div>

        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-xl shadow-xl p-6 animate-fadeIn">
          {/* Header */}
          <div className="flex items-center mb-4">
            <img
              src={ImagePaths.backArrow.default}
              alt="Back"
              className="h-5 w-5 cursor-pointer mr-2"
              onClick={() => navigate("/forgotpassword", { state: { id, email } })}
            />
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
              Verify Your OTP
            </h2>
          </div>

          <p className="text-[13px] text-gray-500 dark:text-gray-400 text-center">
            A confirmation code has been sent to
          </p>
          <p className="text-[13px] text-gray-700 dark:text-gray-200 font-medium text-center mb-5">
            {email || "Registered Mail ID"}
          </p>

          {/* OTP Input */}
          <div className="flex justify-center mb-4">
            <CustomOtpInput numInputs={4} onChange={handleOtpChange} />
          </div>
          <p className="text-[13px] text-gray-500 dark:text-gray-400 text-center mb-4">
            Please enter the code for verification
          </p>

          {/* Verify Button */}
          <button
            onClick={onFinish}
            disabled={loading || otp.length !== 4}
            className={`w-full py-2 px-4 rounded-lg text-white font-semibold transition ${loading || otp.length !== 4
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-[#04b7b1] hover:bg-[#04b7b1]/80 focus:ring-2 focus:ring-[#04b7b1] focus:ring-offset-2"
              }`}
          >
            {loading ? "Verifying OTP..." : "Verify OTP"}
          </button>

          {/* Resend */}
          <div
            className={`text-center mt-5 text-[13px] font-medium cursor-pointer ${timerRunning
                ? "text-gray-400 cursor-not-allowed"
                : "text-[#04b7b1] hover:text-[#04b7b1]/80"
              }`}
            onClick={handleResend}
          >
            Haven’t received code? Send again
          </div>

          {/* Timer */}
          <p className="text-[13px] text-gray-500 dark:text-gray-400 text-center mt-2">
            ⏱ 00 : {seconds < 10 ? `0${seconds}` : seconds}
          </p>
        </div>
      </div>
    </div>
  );
};

export default OtpVerification;
